import * as React from 'react';

import {
  Button,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogTitle,
  TextField,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { LucideAlertTriangle } from 'lucide-react';

import {
  $closeResetOrganizationDialog,
  $resetOrganization,
  $resetOrganizationDialogOpen,
  $resetOrganizationTarget,
} from '../../_state/organizationDialog.state';
import { useTranslations } from '../../i18n/useTranslations';

export const ResetOrganizationDialog: React.FC = () => {
  const t = useTranslations();
  const toast = useToast();

  const [isOpen, setIsOpen] = useAtom($resetOrganizationDialogOpen);
  const target = useAtomValue($resetOrganizationTarget);
  const [mutationState, resetOrganization] = useAtom($resetOrganization);
  const closeDialog = useSetAtom($closeResetOrganizationDialog);

  const [confirmText, setConfirmText] = React.useState('');
  const isResetting = mutationState.isPending;

  const handleReset = React.useCallback(async () => {
    if (confirmText !== 'RESET' || !target) return;

    try {
      const result = await resetOrganization({ organizationId: target.id });

      if (result.coreOrganizationReset?.success) {
        toast.success(t.resetOrganizationDialog.success);
      } else {
        throw new Error('Reset failed');
      }
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : t.resetOrganizationDialog.error,
      );
    }
  }, [confirmText, target, resetOrganization, toast, t]);

  const handleOpenChange = (open: boolean) => {
    if (!isResetting) {
      setConfirmText('');
      if (!open) {
        closeDialog();
      } else {
        setIsOpen(true);
      }
    }
  };

  return (
    <DialogOverlay
      isOpen={isOpen}
      onOpenChange={handleOpenChange}
      className="@ResetOrganizationDialog"
    >
      <DialogContent className="min-w-[500px]">
        <DialogHeader slot="title">
          <DialogTitle>
            <div className="flex items-center gap-2 text-destructive">
              <LucideAlertTriangle className="size-5" />
              {t.resetOrganizationDialog.title}
            </div>
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4 p-4">
          <div className="rounded-lg border border-destructive/20 bg-destructive/5 p-4">
            <div className="flex gap-2">
              <LucideAlertTriangle className="mt-0.5 size-5 shrink-0 text-destructive" />
              <div className="text-sm text-destructive">
                <strong>{t.resetOrganizationDialog.warningTitle}</strong>{' '}
                {t.resetOrganizationDialog.warningDescription(
                  target?.name ?? '',
                )}
              </div>
            </div>
          </div>

          <div>
            <p className="mb-2 text-sm text-neutral">
              {t.resetOrganizationDialog.consequencesTitle}
            </p>
            <ul className="ml-4 list-disc space-y-1 text-sm text-neutral">
              {t.resetOrganizationDialog.consequences.map((item, i) => (
                <li key={i}>{item}</li>
              ))}
            </ul>
          </div>

          <div className="flex flex-col gap-2">
            <TextField
              label={t.resetOrganizationDialog.confirmLabel}
              value={confirmText}
              onChange={setConfirmText}
              placeholder="RESET"
              isDisabled={isResetting}
              autoComplete="off"
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            color="neutral"
            onPress={() => handleOpenChange(false)}
            isDisabled={isResetting}
          >
            {t.resetOrganizationDialog.actions.cancel}
          </Button>
          <Button
            variant="solid"
            color="failure"
            onPress={handleReset}
            isDisabled={confirmText !== 'RESET' || isResetting}
            isLoading={isResetting}
          >
            {t.resetOrganizationDialog.actions.reset}
          </Button>
        </DialogFooter>
      </DialogContent>
    </DialogOverlay>
  );
};
